import React, { useState, useEffect } from 'react';
// import { useTranslation } from 'react-i18next';

interface PaymentGateway {
  name: string;
  displayName: string;
  enabled: boolean;
  priority: number;
  config: {
    features: {
      upi_qr: boolean;
      payment_links: boolean;
      webhooks: boolean;
      refunds: boolean;
      test_mode: boolean;
    };
  };
}

interface PaymentGatewayConfigProps {
  onClose: () => void;
}

const PaymentGatewayConfig: React.FC<PaymentGatewayConfigProps> = ({ onClose }) => {
  // const { t } = useTranslation();
  const [gateways, setGateways] = useState<Record<string, PaymentGateway>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchGatewayConfigurations();
  }, []);

  const fetchGatewayConfigurations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payment-gateway/configurations`);
      const data = await response.json();
      
      if (data.success) {
        setGateways(data.data);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to fetch gateway configurations' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch gateway configurations' });
    } finally {
      setLoading(false);
    }
  };

  const updateGatewayStatus = async (gatewayName: string, enabled: boolean) => {
    try {
      setSaving(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payment-gateway/${gatewayName}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGateways(prev => ({
          ...prev,
          [gatewayName]: {
            ...prev[gatewayName],
            enabled
          }
        }));
        setMessage({ type: 'success', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update gateway status' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update gateway status' });
    } finally {
      setSaving(false);
    }
  };

  const updateGatewayPriority = async (gatewayName: string, priority: number) => {
    try {
      setSaving(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payment-gateway/${gatewayName}/priority`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ priority }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGateways(prev => ({
          ...prev,
          [gatewayName]: {
            ...prev[gatewayName],
            priority
          }
        }));
        setMessage({ type: 'success', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update gateway priority' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update gateway priority' });
    } finally {
      setSaving(false);
    }
  };

  const testGatewayConnection = async (gatewayName: string) => {
    try {
      setTesting(gatewayName);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payment-gateway/${gatewayName}/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: `${gatewayName} connection test successful` });
      } else {
        setMessage({ type: 'error', text: `${gatewayName} connection test failed: ${data.error}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `${gatewayName} connection test failed` });
    } finally {
      setTesting(null);
    }
  };

  const getStatusColor = (enabled: boolean) => {
    return enabled ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
  };

  const getPriorityColor = (priority: number) => {
    if (priority === 1) return 'text-blue-600 bg-blue-100';
    if (priority === 2) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading gateway configurations...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Payment Gateway Configuration</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {message && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-100 text-green-700 border border-green-200' 
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        <div className="space-y-6">
          {Object.entries(gateways).map(([gatewayName, gateway]) => (
            <div key={gatewayName} className="border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">{gateway.displayName}</h3>
                  <p className="text-gray-600">Gateway ID: {gatewayName}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(gateway.enabled)}`}>
                    {gateway.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(gateway.priority)}`}>
                    Priority {gateway.priority}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-700 mb-3">Status Control</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Enable/Disable Gateway</span>
                      <button
                        onClick={() => updateGatewayStatus(gatewayName, !gateway.enabled)}
                        disabled={saving}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          gateway.enabled
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {gateway.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Priority</span>
                      <select
                        value={gateway.priority}
                        onChange={(e) => updateGatewayPriority(gatewayName, parseInt(e.target.value))}
                        disabled={saving}
                        className="px-3 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value={1}>1 (Primary)</option>
                        <option value={2}>2 (Secondary)</option>
                        <option value={3}>3 (Tertiary)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-3">Features</h4>
                  <div className="space-y-2">
                    {Object.entries(gateway.config.features).map(([feature, enabled]) => (
                      <div key={feature} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 capitalize">
                          {feature.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {enabled ? 'Supported' : 'Not Supported'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => testGatewayConnection(gatewayName)}
                  disabled={testing === gatewayName || !gateway.enabled}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    gateway.enabled
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  } ${testing === gatewayName ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {testing === gatewayName ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Testing...
                    </div>
                  ) : (
                    'Test Connection'
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Configuration Notes</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Only enabled gateways will be available for payment processing</li>
              <li>• Priority 1 is the primary gateway used by default</li>
              <li>• Test connections to verify gateway credentials and connectivity</li>
              <li>• Changes take effect immediately</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentGatewayConfig;
